import { useMemo, useRef, useState } from 'react';
import { CreateTaskDialog } from '../components/CreateTaskDialog';
import { TaskCard } from '../components/TaskCard';
import { TaskDialog } from '../components/TaskDialog';
import { COLUMNS, placeInColumn, sortColumn } from '../lib/board';
import { can } from '../lib/permissions';
import { isAdmin } from '../lib/roles';
import { TASK_STATUSES, type Task, type TaskStatus } from '../lib/types';
import { useStore } from '../store/useStore';
import { ViewMenu } from './BoardView';

/** A place a dragged card could be let go: which column, and how far down it. */
type Slot = { status: TaskStatus; index: number };

export function Board() {
  const tasks = useStore((s) => s.tasks);
  const fields = useStore((s) => s.fields);
  const role = useStore((s) => s.role);
  const access = useStore((s) => s.access);
  const layout = useStore((s) => s.layout);
  const saveLayout = useStore((s) => s.saveLayout);
  const pendingTaskIds = useStore((s) => s.pendingTaskIds);
  const moveTask = useStore((s) => s.moveTask);
  const updateTask = useStore((s) => s.updateTask);

  const mayCreate = can(access, 'task.create');
  // Rearranging the board is the same permission as working through it: without
  // it a card is a thing to read, so it does not pick up under the cursor.
  const mayMove = can(access, 'task.claim');
  const compact = layout.density === 'compact';

  const [openTaskId, setOpenTaskId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // The card being dragged, and the slot it would land in.
  const [dragId, setDragId] = useState<string | null>(null);
  const [over, setOver] = useState<Slot | null>(null);
  // `dragover` fires continuously, and `drop` needs the last slot without waiting
  // on a render — so the slot is mirrored into a ref and read from there.
  const overRef = useRef<Slot | null>(null);

  const fieldById = useMemo(() => new Map(fields.map((f) => [f.id, f])), [fields]);
  const pending = useMemo(() => new Set(pendingTaskIds), [pendingTaskIds]);

  // Released tasks are history; the board shows only live work.
  const active = useMemo(() => tasks.filter((t) => !t.versionId && !t.deletedAt), [tasks]);

  /**
   * The field filter lives in the member's layout rather than in local state, so
   * the board opens where the admin pointed it and stays wherever its owner last
   * left it. A filter naming a field they have since been taken out of would show
   * an empty board, so it falls back to everything.
   */
  const fieldFilter = useMemo(() => {
    const stored = layout.boardField;
    if (stored === 'all' || stored === 'none') return stored;
    return fields.some((field) => field.id === stored) ? stored : 'all';
  }, [layout.boardField, fields]);

  const setFieldFilter = (value: string): void => void saveLayout({ boardField: value });

  const columns = useMemo(
    () => COLUMNS.filter((column) => layout.boardColumns.includes(column.status)),
    [layout.boardColumns],
  );

  const visible = useMemo(() => {
    if (fieldFilter === 'all') return active;
    if (fieldFilter === 'none') return active.filter((t) => t.fieldId === null);
    return active.filter((t) => t.fieldId === fieldFilter);
  }, [active, fieldFilter]);

  const byStatus = useMemo(() => {
    const map = new Map<TaskStatus, Task[]>(TASK_STATUSES.map((s) => [s, []]));
    for (const task of visible) map.get(task.status)?.push(task);
    for (const status of TASK_STATUSES) map.set(status, sortColumn(map.get(status) ?? [], status));
    return map;
  }, [visible]);

  const openTask = openTaskId ? tasks.find((t) => t.id === openTaskId) : undefined;

  function endDrag(): void {
    setDragId(null);
    setOver(null);
    overRef.current = null;
  }

  /** Records the slot under the cursor, re-rendering only when it actually moves. */
  function markSlot(status: TaskStatus, index: number): void {
    const current = overRef.current;
    if (current?.status === status && current.index === index) return;
    overRef.current = { status, index };
    setOver(overRef.current);
  }

  /**
   * A drop. Done is a special case: its order is when things were finished, not
   * where they were let go, so only the status is written.
   */
  function drop(status: TaskStatus): void {
    const id = dragId;
    const slotAtDrop = overRef.current;
    endDrag();
    if (!id) return;

    const task = tasks.find((t) => t.id === id);
    if (!task) return;

    if (status === 'done') {
      if (task.status !== 'done') void updateTask(id, { status: 'done' });
      return;
    }

    const column = byStatus.get(status) ?? [];
    const slot = slotAtDrop?.status === status ? slotAtDrop.index : column.length;
    const from = column.findIndex((t) => t.id === id);
    // The slot was measured against the column as rendered, which still contains
    // the card being dragged when it has not left its own column.
    const index = from !== -1 && from < slot ? slot - 1 : slot;
    if (task.status === status && from === index) return;

    const without = column.filter((t) => t.id !== id);
    void moveTask(id, status, placeInColumn(without, index));
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-2">
        <FilterChip
          active={fieldFilter === 'all'}
          onClick={() => setFieldFilter('all')}
          label="All fields"
          count={active.length}
        />
        {fields.map((field) => (
          <FilterChip
            key={field.id}
            active={fieldFilter === field.id}
            onClick={() => setFieldFilter(field.id)}
            label={field.name}
            color={field.color}
            count={active.filter((t) => t.fieldId === field.id).length}
          />
        ))}
        {isAdmin(role) && active.some((t) => t.fieldId === null) && (
          <FilterChip
            active={fieldFilter === 'none'}
            onClick={() => setFieldFilter('none')}
            label="Unsorted"
            count={active.filter((t) => t.fieldId === null).length}
          />
        )}

        <ViewMenu />
      </div>

      {fields.length === 0 ? (
        <EmptyState
          title="No dev fields yet"
          body={
            isAdmin(role)
              ? 'Create your first field in Settings — for example Plugins, Web or Infrastructure.'
              : 'An admin needs to create a dev field and add you to it before tasks show up here.'
          }
        />
      ) : (
        <>
          <div className="flex flex-col items-center gap-1.5">
            {mayCreate && (
              <button
                type="button"
                onClick={() => setCreating(true)}
                className="btn btn-primary !px-5 !py-2.5"
              >
                <svg viewBox="0 0 16 16" className="size-4" fill="currentColor" aria-hidden="true">
                  <path d="M8 3a.75.75 0 0 1 .75.75v3.5h3.5a.75.75 0 0 1 0 1.5h-3.5v3.5a.75.75 0 0 1-1.5 0v-3.5h-3.5a.75.75 0 0 1 0-1.5h3.5v-3.5A.75.75 0 0 1 8 3Z" />
                </svg>
                Add task
              </button>
            )}
            {active.length > 0 && mayMove && (
              <p className="text-[11px] text-ink-faint">
                Drag a card by its grip to move it, or tick it off to finish it.
              </p>
            )}
          </div>

          <div
            className={`grid md:grid-cols-2 xl:grid-cols-4 ${compact ? 'gap-2.5' : 'gap-4'}`}
          >
            {columns.map((column) => {
              const columnTasks = byStatus.get(column.status) ?? [];
              const ordered = column.status !== 'done';
              const isTarget = over?.status === column.status;

              return (
                <section
                  key={column.status}
                  onDragOver={(event) => {
                    if (!dragId || !mayMove) return;
                    event.preventDefault();
                    markSlot(column.status, columnTasks.length);
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    drop(column.status);
                  }}
                  className={`flex min-w-0 flex-col gap-3 rounded-xl border border-dashed p-1 transition-colors ${
                    isTarget && !ordered
                      ? 'border-done/60 bg-done/5'
                      : isTarget
                        ? 'border-line-strong'
                        : 'border-transparent'
                  }`}
                >
                  <header className="flex items-center gap-2 px-1.5">
                    <span className={`size-1.5 rounded-full ${column.accent}`} />
                    <h2 className="text-xs font-semibold tracking-wide text-ink-soft uppercase">
                      {column.label}
                    </h2>
                    <span className="text-xs text-ink-faint">{columnTasks.length}</span>
                  </header>

                  <div className="flex flex-col gap-2">
                    {columnTasks.map((task, index) => (
                      <div key={task.id} className="flex flex-col gap-2">
                        {ordered && isTarget && over.index === index && <DropLine />}
                        <TaskCard
                          task={task}
                          field={task.fieldId ? fieldById.get(task.fieldId) : undefined}
                          compact={compact}
                          unsynced={pending.has(task.id)}
                          isDragging={dragId === task.id}
                          onOpen={() => setOpenTaskId(task.id)}
                          onDragStart={() => setDragId(task.id)}
                          onDragEnd={endDrag}
                          onDragOver={(event) => {
                            if (!dragId || !mayMove) return;
                            event.preventDefault();
                            // Otherwise the column's own handler would overwrite the
                            // precise slot with "at the end".
                            event.stopPropagation();
                            const rect = event.currentTarget.getBoundingClientRect();
                            const below = event.clientY > rect.top + rect.height / 2;
                            markSlot(column.status, below ? index + 1 : index);
                          }}
                        />
                      </div>
                    ))}

                    {ordered && isTarget && over.index >= columnTasks.length && <DropLine />}

                    {columnTasks.length === 0 && (
                      <p className="rounded-lg border border-dashed border-line px-3 py-6 text-center text-xs text-ink-faint">
                        {dragId ? 'Drop here' : 'Nothing here'}
                      </p>
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        </>
      )}

      {creating && (
        <CreateTaskDialog
          defaultFieldId={
            fieldFilter === 'all' || fieldFilter === 'none' ? null : fieldFilter
          }
          onClose={() => setCreating(false)}
        />
      )}
      {openTask && <TaskDialog task={openTask} onClose={() => setOpenTaskId(null)} />}
    </div>
  );
}

/** Where the card would land. */
function DropLine() {
  return <span aria-hidden="true" className="h-0.5 rounded-full bg-accent" />;
}

function FilterChip({
  active,
  onClick,
  label,
  count,
  color,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
  color?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition-colors ${
        active
          ? 'border-line-strong bg-elevated text-ink'
          : 'border-line text-ink-soft hover:bg-hover hover:text-ink'
      }`}
    >
      {color && <span className="size-1.5 rounded-full" style={{ backgroundColor: color }} />}
      <span className="font-medium">{label}</span>
      <span className="text-ink-faint">{count}</span>
    </button>
  );
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="card p-8 text-center">
      <p className="text-sm font-medium">{title}</p>
      <p className="mx-auto mt-1.5 max-w-sm text-sm leading-relaxed text-ink-faint">{body}</p>
    </div>
  );
}
